import { KeyboardEvent, useRef } from 'react';
import styled from 'styled-components';

import appendUserMention from 'components/shared/activityInput/appendUserMention';
import { TaggableTextArea } from 'components/shared/activityInput/TaggableTextArea';
import TaggableUserMenu, { TaggableUserMenuItemProps } from 'components/shared/activityInput/TaggableUserMenu';
import Button from 'components/shared/Button';
import Checkbox from 'components/shared/form/Checkbox';
import { ChatDraft } from 'services/api/chat';
import { TaggableUserResource } from 'services/api/taggableUsers';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

type Props = {
  values: ChatDraft;
  onChange: (values: ChatDraft) => void;
  onSend: () => void;
  // Terminal (completed/canceled) referral → read-only. The server 422 is the backstop.
  disabled: boolean;
  isSending: boolean;
  // Which conversation's members are taggable (the consolidated /taggable_users endpoint).
  taggableUserResource?: TaggableUserResource;
  // "Send to all facilities" is shown only for multi-facility referrals (conversations.length
  // > 1); single-facility users never see it, matching the mockups.
  canBroadcast: boolean;
  broadcast: boolean;
  onBroadcastChange: (value: boolean) => void;
  // The per-recipient mention 422, rendered inline (the draft is kept) instead of a toast.
  mentionError?: string | null;
};

// The chat composer: a taggable text area (the shared mention kit — never forked), an
// optional broadcast toggle, and Send. No Attach (cut from v1). Enter sends,
// Shift+Enter newlines.
export default function Composer({
  values,
  onChange,
  onSend,
  disabled,
  isSending,
  taggableUserResource,
  canBroadcast,
  broadcast,
  onBroadcastChange,
  mentionError,
}: Props) {
  const textAreaRef = useRef<any>(null);

  const hasTags = values.mentions.length > 0;
  // Mentions ↔ broadcast are mutually exclusive; tagging is also off when read-only.
  const taggable = !disabled && !broadcast;
  const canSend = values.plaintext.trim().length > 0 && !disabled && !isSending;

  const submit = () => {
    if (canSend) onSend();
  };

  // react-mentions stops Enter's propagation while its suggestion overlay is open, so a
  // bubbled Enter here means the overlay is closed — send.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.defaultPrevented) {
      event.preventDefault();
      submit();
    }
  };

  const handleTagClick = (user: TaggableUserMenuItemProps) => {
    textAreaRef.current?.focus();
    onChange(appendUserMention(values, user));
  };

  return (
    <Container data-testid='chat-composer'>
      <Body onKeyDown={handleKeyDown}>
        <TaggableTextArea
          values={values}
          onChange={(next) => onChange({ ...values, ...next })}
          placeholder={disabled ? 'This conversation is read-only.' : 'Start typing here to add a note'}
          disabled={disabled}
          inputRef={textAreaRef}
          taggable={taggable}
          taggableUserResource={taggableUserResource}
        />
      </Body>
      {mentionError && <MentionError data-testid='chat-mention-error'>{mentionError}</MentionError>}
      <ActionsBar>
        {/* Tagging is unavailable while broadcasting/read-only — the affordance hides, the hint explains. */}
        {taggable && (
          <TagSlot>
            <TaggableUserMenu taggableUserResource={taggableUserResource} onMenuItemClick={handleTagClick} />
          </TagSlot>
        )}
        {/* Shown in BOTH exclusive states: while broadcasting (Tag is hidden) and while
            tagged (the checkbox below is disabled) — the same one-line reason for each. */}
        {canBroadcast && (broadcast || hasTags) && (
          <BroadcastHint data-testid='chat-tag-broadcast-hint'>Tags can’t be sent to all facilities</BroadcastHint>
        )}
        {canBroadcast && (
          <BroadcastToggle data-testid='chat-broadcast-toggle'>
            <Checkbox
              checked={broadcast}
              disabled={disabled || hasTags}
              label='Send to all facilities'
              labelSize='12px'
              labelMargin='8px'
              size={18}
              checkedColor={colors.blue100}
              onChange={onBroadcastChange}
            />
          </BroadcastToggle>
        )}
        <SubmitButton data-testid='chat-send' disabled={!canSend} loading={isSending} onClick={submit}>
          Send
        </SubmitButton>
      </ActionsBar>
    </Container>
  );
}

const Container = styled.div`
  margin-top: 16px;
  background-color: ${colors.white};
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  border: 1px solid ${colors.black10};
  display: flex;
  flex-direction: column;

  &:focus-within {
    border-color: ${colors.blue100};
  }
`;

// Wraps the react-mentions field (same padding/min-height as the old textarea) and carries
// the Enter-to-send keydown.
const Body = styled.div`
  padding: 16px;
  min-height: 72px;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  line-height: 24px;
  color: ${colors.black100};
`;

const MentionError = styled(BodySmall)`
  padding: 0 16px 8px;
  color: ${colors.red100};
`;

// Right-aligned by default (hint/toggle/Send); the Tag slot, when rendered, anchors left.
const ActionsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  border-top: 1px solid ${colors.black10};
  padding: 8px 16px;
`;

const TagSlot = styled.div`
  margin-right: auto;
`;

const BroadcastHint = styled(BodySmall)`
  color: ${colors.black50};
`;

const BroadcastToggle = styled.div`
  display: flex;
  align-items: center;
`;

const SubmitButton = styled(Button)`
  padding: 3px 18px;
  font-size: 10px;
  line-height: 14px;
  border: 2px solid transparent;
`;
